/**
 * One Line Calendar - Main (init, state, events)
 * Depends on: one-line-calendar-core.js, one-line-calendar-widget.js
 */
(function() {
  'use strict';

  if (!window.HanaleiBooking || !window.HanaleiBooking.utils || !window.HanaleiBooking.widget) {
    console.error('One Line Calendar: core or widget not loaded');
    return;
  }

  var utils = window.HanaleiBooking.utils;
  var storage = window.HanaleiBooking.storage;
  var api = window.HanaleiBooking.api;
  var w = window.HanaleiBooking.widget;

  var sectionHandlers = [];

  if (!window.HanaleiBooking._docListenersInstalled) {
    window.HanaleiBooking._docListenersInstalled = true;
    document.addEventListener('click', function(e) {
      for (var i = 0; i < sectionHandlers.length; i++) { sectionHandlers[i].click(e); }
    });
    document.addEventListener('keydown', function(e) {
      for (var i = 0; i < sectionHandlers.length; i++) { sectionHandlers[i].keydown(e); }
    });
  }

  function init(section) {
    if (!section) return;

    var config = parseConfig(section);
    var str = {
      datePlaceholder: config.datePlaceholder,
      noteText: config.noteText,
      bookButtonText: config.bookButtonText
    };
    var elements = queryElements(section);
    var state = createState();

    function refreshUI() {
      w.updateGuestsUI(state, config, elements);
      w.updateDateFieldsUI(state, elements, str);
      w.updatePriceUI(state, config, elements, str);
    }

    var saved = storage.load();
    var apiConfigured = api.isConfigured(config.listingId, config.apiBase);

    if (saved && applyPreferences(saved, state, config, elements, str)) {
      if (apiConfigured) {
        var spanMonths = monthsCoveringCheckInToLastNight(state.checkinDate, state.checkoutDate);
        if (spanMonths > 24) {
          clearRestoredBookingPrefs(state, storage, elements, str, config);
          refreshUI();
        } else {
          section.classList.add('one-line-calendar--price-loading');
          w.toggleNoteTable(elements, true);
          var nights = utils.getNightsBetween(state.checkinDate, state.checkoutDate);
          if (elements.priceLabel) elements.priceLabel.style.display = 'none';
          if (elements.nightsLabel && nights > 0) elements.nightsLabel.textContent = ' for ' + nights + ' night' + (nights > 1 ? 's' : '');
          Promise.all([
            api.fetchRange(config.apiBase, config.listingId, state.currentYear, state.currentMonth, spanMonths),
            api.fetchCleaningFee(config.apiBase, config.listingId)
          ]).then(function() {
            section.classList.remove('one-line-calendar--price-loading');
            if (
              state.checkinDate &&
              state.checkoutDate &&
              !isSavedRangeStrictlyAvailable(state.checkinDate, state.checkoutDate, config.listingId)
            ) {
              clearRestoredBookingPrefs(state, storage, elements, str, config);
            }
            refreshUI();
            w.renderCalendar(state, config, elements);
          });
        }
      } else {
        refreshUI();
      }
    } else {
      refreshUI();
    }

    w.renderCalendar(state, config, elements);

    bindAll(state, config, elements, str);
  }

  function parseConfig(section) {
    var ds = section.dataset;
    return {
      pricePerNight: parseFloat(ds.pricePerNight) || 900,
      maxGuests: parseInt(ds.maxGuests, 10) || 6,
      minNights: parseInt(ds.minNights, 10) || 1,
      listingId: (ds.listingId || '').trim(),
      apiBase: (ds.apiBase || '').replace(/\/+$/, ''),
      datePlaceholder: ds.datePlaceholder || 'Select date',
      noteText: ds.noteText || 'Use the calendar to select your arrival and departure dates.',
      bookButtonText: ds.bookButtonText || 'Book'
    };
  }

  function queryElements(section) {
    var q = function(s) { return section.querySelector(s); };
    return {
      checkinField: q('[data-date-field="checkin"]'),
      checkoutField: q('[data-date-field="checkout"]'),
      checkinValue: q('[data-checkin-value]'),
      checkoutValue: q('[data-checkout-value]'),
      guestsText: q('[data-guests-text]'),
      guestsCount: q('[data-guests-count]'),
      guestsDecrease: q('[data-guests-decrease]'),
      guestsIncrease: q('[data-guests-increase]'),
      bookingNote: q('[data-booking-note]'),
      bookingNoteText: q('[data-booking-note] .one-line-calendar__note-text'),
      priceTable: q('[data-price-table]'),
      bookingFee: q('[data-booking-fee]'),
      cleaningFeeEl: q('[data-cleaning-fee]'),
      total: q('[data-total]'),
      bookingNoteMobile: q('[data-booking-note-mobile]'),
      bookingNoteTextMobile: q('[data-booking-note-mobile] .one-line-calendar__note-text'),
      priceTableMobile: q('[data-price-table-mobile]'),
      bookingFeeMobile: q('[data-booking-fee-mobile]'),
      cleaningFeeElMobile: q('[data-cleaning-fee-mobile]'),
      totalMobile: q('[data-total-mobile]'),
      bookBtn: q('[data-book-btn]'),
      priceLabel: q('[data-price-label]'),
      priceDisplay: q('[data-price-display]'),
      nightsLabel: q('[data-nights-label]'),
      calendar: q('[data-calendar]'),
      calendarMonth: q('[data-calendar-month]'),
      calendarGrid: q('[data-calendar-grid]'),
      calendarPrev: q('[data-calendar-prev]'),
      calendarNext: q('[data-calendar-next]'),
      calendarClear: q('[data-calendar-clear]'),
      bookingPopup: q('[data-booking-popup]'),
      bookingPopupBackdrop: q('[data-booking-popup-backdrop]'),
      bookingPopupClose: q('[data-booking-popup-close]'),
      bookingPopupSubmit: q('[data-booking-popup-submit]'),
      bookingPopupContent: q('.booking-popup__content'),
      bookingPopupScrollIndicator: q('[data-booking-popup-scroll-indicator]'),
      adultsCount: q('[data-adults-count]'),
      adultsDecrease: q('[data-adults-decrease]'),
      adultsIncrease: q('[data-adults-increase]'),
      childrenCount: q('[data-children-count]'),
      childrenDecrease: q('[data-children-decrease]'),
      childrenIncrease: q('[data-children-increase]'),
      popupBookingFee: q('[data-popup-booking-fee]'),
      popupCleaningFee: q('[data-popup-cleaning-fee]'),
      popupTotal: q('[data-popup-total]'),
      popupCheckinDate: q('[data-popup-checkin-date]'),
      popupCheckoutDate: q('[data-popup-checkout-date]'),
      popupNights: q('[data-popup-nights]'),
      popupNightsCount: q('[data-popup-nights-count]'),
      bookingError: q('[data-booking-error]'),
      bookingErrorText: q('[data-booking-error-text]')
    };
  }

  function createState() {
    var n = new Date();
    return {
      guests: 1, adults: 1, children: 0,
      checkinDate: null, checkoutDate: null,
      calendarOpen: false, bookingPopupOpen: false, selectingField: null,
      currentMonth: n.getMonth(), currentYear: n.getFullYear()
    };
  }

  function monthsCoveringCheckInToLastNight(ci, co) {
    var lastNight = new Date(co.getTime());
    lastNight.setHours(0, 0, 0, 0);
    lastNight.setDate(lastNight.getDate() - 1);
    var ciNorm = new Date(ci.getTime());
    ciNorm.setHours(0, 0, 0, 0);
    if (lastNight < ciNorm) return 1;
    var y1 = ciNorm.getFullYear();
    var m1 = ciNorm.getMonth();
    var y2 = lastNight.getFullYear();
    var m2 = lastNight.getMonth();
    return (y2 - y1) * 12 + (m2 - m1) + 1;
  }

  function clearRestoredBookingPrefs(state, storageRef, elements, str, config) {
    storageRef.clear();
    state.checkinDate = null;
    state.checkoutDate = null;
    state.guests = 1;
    state.adults = 1;
    state.children = 0;
    var now = new Date();
    state.currentMonth = now.getMonth();
    state.currentYear = now.getFullYear();
    w.updateDateFieldsUI(state, elements, str);
    w.updateGuestsUI(state, config, elements);
  }

  function isSavedRangeStrictlyAvailable(ci, co, listingId) {
    var d = new Date(ci.getTime());
    d.setHours(0, 0, 0, 0);
    var end = new Date(co.getTime());
    end.setHours(0, 0, 0, 0);
    while (d < end) {
      if (!api.isNightAvailableInCache(d, listingId)) return false;
      d.setDate(d.getDate() + 1);
    }
    return true;
  }

  function applyPreferences(pref, state, config, elements, str) {
    if (!pref) return false;
    try {
      var ci = new Date(pref.checkinDate);
      var co = new Date(pref.checkoutDate);
      if (isNaN(ci.getTime()) || isNaN(co.getTime()) || co <= ci) return false;
      var today = new Date();
      today.setHours(0, 0, 0, 0);
      if (ci < today) return false;

      state.checkinDate = ci;
      state.checkoutDate = co;
      state.guests = Math.min(Math.max(1, pref.guests), config.maxGuests);
      if (state.checkinDate) {
        state.currentMonth = state.checkinDate.getMonth();
        state.currentYear = state.checkinDate.getFullYear();
      }

      w.updateDateFieldsUI(state, elements, str);
      w.updateGuestsUI(state, config, elements);
      return true;
    } catch (e) {
      console.warn('Could not apply booking preferences:', e);
      return false;
    }
  }

  function openCalendar(field, state, elements) {
    state.calendarOpen = true;
    state.selectingField = field;
    if (elements.calendar) elements.calendar.classList.add('calendar-popup--open');
  }

  function closeCalendar(state, elements) {
    state.calendarOpen = false;
    state.selectingField = null;
    if (elements.calendar) elements.calendar.classList.remove('calendar-popup--open');
  }

  function isRangeFullyAvailable(ci, co, config) {
    if (!api.isConfigured(config.listingId, config.apiBase)) return true;
    var d = new Date(ci.getTime());
    while (d < co) {
      if (api.isDateUnavailable(d, config.listingId)) return false;
      d.setDate(d.getDate() + 1);
    }
    return true;
  }

  function selectDate(dateStr, state, config, elements, str) {
    var date = new Date(dateStr);
    if (state.selectingField === 'checkin') {
      if (state.checkoutDate && date < state.checkoutDate) {
        if (!isRangeFullyAvailable(date, state.checkoutDate, config)) return;
        state.checkinDate = date;
        closeCalendar(state, elements);
        w.updateDateFieldsUI(state, elements, str);
        if (api.isConfigured(config.listingId, config.apiBase)) {
          api.fetchCleaningFee(config.apiBase, config.listingId).then(function() {
            w.updatePriceUI(state, config, elements, str);
          });
        }
      } else {
        state.checkinDate = date;
        if (state.checkoutDate && state.checkoutDate <= date) state.checkoutDate = null;
        state.selectingField = 'checkout';
        w.updateDateFieldsUI(state, elements, str);
        w.renderCalendar(state, config, elements);
      }
    } else if (state.selectingField === 'checkout') {
      if (!state.checkinDate) {
        state.checkoutDate = date;
        state.selectingField = 'checkin';
        w.updateDateFieldsUI(state, elements, str);
        w.renderCalendar(state, config, elements);
      } else if (date > state.checkinDate) {
        if (!isRangeFullyAvailable(state.checkinDate, date, config)) return;
        state.checkoutDate = date;
        closeCalendar(state, elements);
        w.updateDateFieldsUI(state, elements, str);
        if (api.isConfigured(config.listingId, config.apiBase)) {
          api.fetchCleaningFee(config.apiBase, config.listingId).then(function() {
            w.updatePriceUI(state, config, elements, str);
          });
        }
      }
    }

    storage.save({ checkinDate: state.checkinDate, checkoutDate: state.checkoutDate, guests: state.guests });
    w.updatePriceUI(state, config, elements, str);
    if (state.bookingPopupOpen && elements.bookingPopupSubmit) {
      w.updatePopupSubmitButton(state, config, elements);
    }
  }

  function fetchRangeOnOpen(config, state) {
    if (!api.isConfigured(config.listingId, config.apiBase)) return Promise.resolve();
    if (!api.needsFetch(state.currentYear, state.currentMonth, config.listingId)) return Promise.resolve();
    return api.fetchRange(config.apiBase, config.listingId, state.currentYear, state.currentMonth, 6);
  }

  function maybePrefetch(state, config, direction) {
    if (!api.isConfigured(config.listingId, config.apiBase)) return Promise.resolve();
    if (!api.needsFetch(state.currentYear, state.currentMonth, config.listingId, direction)) return Promise.resolve();
    var y = state.currentYear;
    var m = state.currentMonth;
    if (direction === 'prev') {
      m -= 5;
      if (m < 0) { m += 12; y -= 1; }
    }
    return api.fetchRange(config.apiBase, config.listingId, y, m, 6);
  }

  function prevMonth(state, config, elements, str) {
    state.currentMonth--;
    if (state.currentMonth < 0) { state.currentMonth = 11; state.currentYear--; }
    var p = maybePrefetch(state, config, 'prev');
    w.renderCalendar(state, config, elements);
    p.then(function() {
      w.renderCalendar(state, config, elements);
      w.updatePriceUI(state, config, elements, str);
    });
  }

  function nextMonth(state, config, elements, str) {
    state.currentMonth++;
    if (state.currentMonth > 11) { state.currentMonth = 0; state.currentYear++; }
    var p = maybePrefetch(state, config, 'next');
    w.renderCalendar(state, config, elements);
    p.then(function() {
      w.renderCalendar(state, config, elements);
      w.updatePriceUI(state, config, elements, str);
    });
  }

  function checkScrollIndicator(elements) {
    if (!elements.bookingPopupContent || !elements.bookingPopupScrollIndicator || !elements.bookingPopupSubmit) return;
    var br = elements.bookingPopupSubmit.getBoundingClientRect();
    var cr = elements.bookingPopupContent.getBoundingClientRect();
    elements.bookingPopupScrollIndicator.classList.toggle('booking-popup__scroll-indicator--visible', !(br.top >= cr.top && br.top <= cr.bottom));
  }

  function openBookingPopup(state, config, elements, str) {
    if (!state.checkinDate || !state.checkoutDate) return;
    state.adults = state.guests;
    state.children = 0;
    state.bookingPopupOpen = true;

    if (elements.bookingPopup) elements.bookingPopup.classList.add('booking-popup--active');
    w.updatePopupGuestsUI(state, config, elements);
    w.updatePriceUI(state, config, elements, str);
    w.updatePopupSubmitButton(state, config, elements);

    document.body.style.overflow = 'hidden';
    setTimeout(function() { checkScrollIndicator(elements); setTimeout(function() { checkScrollIndicator(elements); }, 100); }, 300);
  }

  function closeBookingPopup(state, elements) {
    state.bookingPopupOpen = false;
    if (elements.bookingPopup) elements.bookingPopup.classList.remove('booking-popup--active');
    if (elements.bookingPopupScrollIndicator) elements.bookingPopupScrollIndicator.classList.remove('booking-popup__scroll-indicator--visible');
    document.body.style.overflow = '';
    if (elements.bookingPopupContent) elements.bookingPopupContent.scrollTop = 0;
  }

  function handleBookingSubmit(state, config, elements) {
    if (elements.bookingPopupSubmit.disabled) return;
    var nights = utils.getNightsBetween(state.checkinDate, state.checkoutDate);
    if (nights > 0 && nights < config.minNights) return;
    if (!api.isConfigured(config.listingId, config.apiBase)) {
      alert('Booking API not configured. Select a cottage in section settings.');
      return;
    }

    if (elements.bookingError) { elements.bookingError.style.display = 'none'; }
    elements.bookingPopupSubmit.disabled = true;
    elements.bookingPopupSubmit.textContent = 'Processing...';

    fetch(config.apiBase + '/api/book', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        listingId: config.listingId,
        checkIn: utils.formatDateISO(state.checkinDate),
        checkOut: utils.formatDateISO(state.checkoutDate),
        guests: state.adults + state.children,
        adults: state.adults,
        children: state.children,
        pets: 0
      })
    })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (data.success && data.checkoutUrl) {
          if (data.quote && typeof data.quote.totalPrice === 'number' && elements.popupTotal) {
            elements.popupTotal.textContent = utils.formatMoney(data.quote.totalPrice);
          }
          window.location.href = data.checkoutUrl;
        } else {
          if (elements.bookingError) { elements.bookingError.style.display = 'block'; if (elements.bookingErrorText) elements.bookingErrorText.textContent = data.error || 'Booking failed. Please try again.'; }
          elements.bookingPopupSubmit.disabled = false;
          elements.bookingPopupSubmit.textContent = 'Proceed to Payment';
        }
      })
      .catch(function(err) {
        console.error('Booking error:', err);
        if (elements.bookingError) { elements.bookingError.style.display = 'block'; if (elements.bookingErrorText) elements.bookingErrorText.textContent = 'Network error. Please check your connection and try again.'; }
        elements.bookingPopupSubmit.disabled = false;
        elements.bookingPopupSubmit.textContent = 'Proceed to Payment';
      });
  }

  function bindAll(state, config, elements, str) {
    str = str || {};
    var apiConfigured = api.isConfigured(config.listingId, config.apiBase);

    if (elements.guestsDecrease) elements.guestsDecrease.addEventListener('click', function() {
      if (state.guests > 1) { state.guests--; w.updateGuestsUI(state, config, elements); storage.save({ checkinDate: state.checkinDate, checkoutDate: state.checkoutDate, guests: state.guests }); }
    });
    if (elements.guestsIncrease) elements.guestsIncrease.addEventListener('click', function() {
      if (state.guests < config.maxGuests) { state.guests++; w.updateGuestsUI(state, config, elements); storage.save({ checkinDate: state.checkinDate, checkoutDate: state.checkoutDate, guests: state.guests }); }
    });

    if (elements.checkinField) elements.checkinField.addEventListener('click', function(e) {
      e.stopPropagation();
      if (state.calendarOpen && state.selectingField === 'checkin') {
        closeCalendar(state, elements);
        w.updateDateFieldsUI(state, elements, str);
      }
      else {
        openCalendar('checkin', state, elements);
        w.updateDateFieldsUI(state, elements, str);
        if (apiConfigured) {
          var p = fetchRangeOnOpen(config, state);
          w.renderCalendar(state, config, elements);
          p.then(function() {
            w.renderCalendar(state, config, elements);
            w.updatePriceUI(state, config, elements, str);
          });
        } else {
          w.renderCalendar(state, config, elements);
        }
      }
    });
    if (elements.checkoutField) elements.checkoutField.addEventListener('click', function(e) {
      e.stopPropagation();
      if (state.calendarOpen && state.selectingField === 'checkout') {
        closeCalendar(state, elements);
        w.updateDateFieldsUI(state, elements, str);
      }
      else {
        openCalendar('checkout', state, elements);
        w.updateDateFieldsUI(state, elements, str);
        if (apiConfigured) {
          var p = fetchRangeOnOpen(config, state);
          w.renderCalendar(state, config, elements);
          p.then(function() {
            w.renderCalendar(state, config, elements);
            w.updatePriceUI(state, config, elements, str);
          });
        } else {
          w.renderCalendar(state, config, elements);
        }
      }
    });

    if (elements.calendar) {
      elements.calendar.addEventListener('click', function(e) { e.stopPropagation(); });
      if (elements.calendarPrev) elements.calendarPrev.addEventListener('click', function() { prevMonth(state, config, elements, str); });
      if (elements.calendarNext) elements.calendarNext.addEventListener('click', function() { nextMonth(state, config, elements, str); });
      if (elements.calendarClear) elements.calendarClear.addEventListener('click', function() {
        state.checkinDate = null; state.checkoutDate = null; storage.clear();
        w.updateDateFieldsUI(state, elements, str); w.updatePriceUI(state, config, elements, str); w.renderCalendar(state, config, elements);
      });
      if (elements.calendarGrid) elements.calendarGrid.addEventListener('click', function(e) {
        var el = e.target.closest('[data-date]');
        if (el) selectDate(el.dataset.date, state, config, elements, str);
      });
    }

    document.addEventListener('click', function() {
      if (state.calendarOpen) {
        closeCalendar(state, elements);
        w.updateDateFieldsUI(state, elements, str);
      }
    });

    if (elements.bookBtn) elements.bookBtn.addEventListener('click', function() {
      if (elements.bookBtn.disabled) return;
      var nights = utils.getNightsBetween(state.checkinDate, state.checkoutDate);
      var minErr = nights > 0 && nights < config.minNights;
      if (state.checkinDate && state.checkoutDate && !minErr) {
        openBookingPopup(state, config, elements, str);
      } else {
        openCalendar('checkin', state, elements);
        w.updateDateFieldsUI(state, elements, str);
        if (apiConfigured) {
          var p = fetchRangeOnOpen(config, state);
          w.renderCalendar(state, config, elements);
          p.then(function() {
            w.renderCalendar(state, config, elements);
            w.updatePriceUI(state, config, elements, str);
          });
        } else {
          w.renderCalendar(state, config, elements);
        }
      }
    });
    if (elements.bookingPopupBackdrop) elements.bookingPopupBackdrop.addEventListener('click', function() { closeBookingPopup(state, elements); });
    if (elements.bookingPopupClose) elements.bookingPopupClose.addEventListener('click', function() { closeBookingPopup(state, elements); });

    if (elements.adultsDecrease) elements.adultsDecrease.addEventListener('click', function() {
      if (state.adults > 1) { state.adults--; w.updatePopupGuestsUI(state, config, elements); w.updatePopupSubmitButton(state, config, elements); w.updatePriceUI(state, config, elements, str); }
    });
    if (elements.adultsIncrease) elements.adultsIncrease.addEventListener('click', function() {
      var t = state.adults + state.children;
      if (t < config.maxGuests) { state.adults++; w.updatePopupGuestsUI(state, config, elements); w.updatePopupSubmitButton(state, config, elements); w.updatePriceUI(state, config, elements, str); }
    });
    if (elements.childrenDecrease) elements.childrenDecrease.addEventListener('click', function() {
      if (state.children > 0) { state.children--; w.updatePopupGuestsUI(state, config, elements); w.updatePopupSubmitButton(state, config, elements); w.updatePriceUI(state, config, elements, str); }
    });
    if (elements.childrenIncrease) elements.childrenIncrease.addEventListener('click', function() {
      var t = state.adults + state.children;
      if (t < config.maxGuests) { state.children++; w.updatePopupGuestsUI(state, config, elements); w.updatePopupSubmitButton(state, config, elements); w.updatePriceUI(state, config, elements, str); }
    });

    if (elements.bookingPopupSubmit) elements.bookingPopupSubmit.addEventListener('click', function() { handleBookingSubmit(state, config, elements); });

    if (elements.bookingPopupContent) {
      elements.bookingPopupContent.addEventListener('click', function(e) { e.stopPropagation(); });
      elements.bookingPopupContent.addEventListener('scroll', function() { checkScrollIndicator(elements); });
    }
    if (elements.bookingPopupScrollIndicator && elements.bookingPopupContent) {
      elements.bookingPopupScrollIndicator.addEventListener('click', function() {
        var h = elements.bookingPopupContent.clientHeight;
        elements.bookingPopupContent.scrollBy({ top: Math.min(h * 0.8, 400), behavior: 'smooth' });
      });
    }

    sectionHandlers.push({
      click: function(e) {
        var inDates = e.target.closest('.one-line-calendar__dates');
        var onBookBtn = e.target.closest('[data-book-btn]');
        if (state.calendarOpen && !inDates && !onBookBtn) {
          closeCalendar(state, elements);
          w.updateDateFieldsUI(state, elements, str);
        }
      },
      keydown: function(e) {
        if (e.key === 'Escape') {
          if (state.calendarOpen) {
            closeCalendar(state, elements);
            w.updateDateFieldsUI(state, elements, str);
          }
          if (state.bookingPopupOpen) closeBookingPopup(state, elements);
        }
      }
    });
  }

  window.HanaleiBooking.init = init;
})();
